import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import {
  fetchManagedUsers,
  updateManagedUserPermissions,
} from './lib/authClient';
import './styles/account.css';

const PERMISSION_LABELS = {
  managePermissions: 'Управление правами',
  publishPosts: 'Публикация постов',
  previewRestrictedFeatures: 'Превью скрытых функций',
};

const formatDateTime = (value) => {
  if (!value) return 'Неизвестно';

  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
};

const PermissionPill = ({ active, label }) => (
  <span className={`account-permission ${active ? 'active' : ''}`}>{label}</span>
);

const AccountPage = () => {
  const {
    token,
    user,
    sessions,
    isLoading,
    isAuthenticated,
    refreshAccount,
    refreshSessions,
    revokeOtherSession,
  } = useAuth();
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [search, setSearch] = useState('');
  const [saveState, setSaveState] = useState('');
  const [sessionState, setSessionState] = useState('');
  const [isSessionsOpen, setIsSessionsOpen] = useState(false);

  const isAdmin = Boolean(user?.permissions?.managePermissions);
  const visibleUsers = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    if (!query) return users;
    return users.filter((entry) => String(entry.login || '').toLowerCase().includes(query));
  }, [search, users]);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin || !token) return undefined;

    let cancelled = false;

    const loadUsers = async () => {
      setUsersLoading(true);
      setUsersError('');

      try {
        const response = await fetchManagedUsers(token);
        if (cancelled) return;
        setUsers(Array.isArray(response?.users) ? response.users : []);
      } catch (error) {
        if (cancelled) return;
        setUsersError(error.message || 'Не удалось загрузить пользователей.');
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isAdmin, token]);

  if (!isLoading && !isAuthenticated) {
    return <Navigate to="/main" replace />;
  }

  const handleRefresh = async () => {
    setSaveState('');
    setSessionState('');
    await refreshAccount();
    await refreshSessions();
  };

  const handleRevokeSession = async (sessionId) => {
    setSessionState('Завершаем выбранную сессию...');

    try {
      await revokeOtherSession(sessionId);
      setSessionState('Сессия успешно завершена.');
    } catch (error) {
      setSessionState(error.message || 'Не удалось завершить сессию.');
    }
  };

  const handlePermissionToggle = async (login, permission, currentValue) => {
    if (!token) return;

    setSaveState(`Сохраняем права для @${login}...`);

    try {
      const response = await updateManagedUserPermissions(token, login, {
        [permission]: !currentValue,
      });

      setUsers((current) => current.map((entry) => (
        entry.login === login ? response.user : entry
      )));
      setSaveState(`Права для @${login} обновлены.`);
    } catch (error) {
      setSaveState(error.message || 'Не удалось обновить права.');
    }
  };

  return (
    <main className="content">
      <section className="account-page">
        <div className="account-hero">
          <div>
            <span className="account-kicker">Аккаунт</span>
            <h1>@{user?.login}</h1>
            <p>
              Здесь собраны настройки аккаунта, активные сессии и, при наличии прав,
              управление доступом пользователей.
            </p>
          </div>
          <div className="account-hero-actions">
            <button type="button" className="account-primary-btn" onClick={handleRefresh}>
              Обновить данные
            </button>
            <Link className="account-secondary-link" to="/main">Вернуться к ленте</Link>
          </div>
        </div>

        <section className="account-grid">
          <article className="account-card">
            <h2>Профиль</h2>
            <div className="account-meta-list">
              <div><strong>Логин:</strong> @{user?.login}</div>
              <div><strong>Email:</strong> {user?.email || 'Не указан'}</div>
              <div><strong>Создан:</strong> {formatDateTime(user?.createdAt)}</div>
              <div><strong>Срок сессии:</strong> {user?.sessionDurationDays || 7} дней</div>
            </div>

            <div className="account-permissions">
              <PermissionPill
                active={Boolean(user?.permissions?.managePermissions)}
                label="Админ сайта"
              />
              <PermissionPill
                active={Boolean(user?.permissions?.publishPosts)}
                label="Публикация"
              />
              <PermissionPill
                active={Boolean(user?.permissions?.previewRestrictedFeatures)}
                label="Превью"
              />
            </div>
          </article>

          <article className="account-card">
            <h2>Сессии</h2>
            {sessionState && <div className="account-status">{sessionState}</div>}
            <div className={`account-session-details ${isSessionsOpen ? 'open' : ''}`}>
              <button
                type="button"
                className="account-session-summary"
                onClick={() => setIsSessionsOpen((current) => !current)}
              >
                <span>Активные сессии</span>
                <span className="account-session-summary-right">
                  <span className="account-session-count">{sessions.length}</span>
                  <span
                    className={`account-session-chevron ${isSessionsOpen ? 'open' : ''}`}
                    aria-hidden="true"
                  >
                    <span className="account-session-chevron-line" />
                    <span className="account-session-chevron-line vertical" />
                  </span>
                </span>
              </button>
              <div className="account-session-panel">
                <div className="account-session-list">
                  {sessions.map((session) => (
                    <div key={session.id} className="account-session-card">
                      <strong>{session.clientLabel || 'Сессия'}</strong>
                      <span>{session.browser || session.platform || 'Устройство не определено'}</span>
                      <span>{session.locationLabel || 'Локация недоступна'}</span>
                      <span>Активность: {formatDateTime(session.lastSeenAt)}</span>
                      <div className="account-session-actions">
                        {session.isCurrent ? (
                          <span className="account-session-badge">Текущая</span>
                        ) : (
                          <button
                            type="button"
                            className="account-session-revoke"
                            onClick={() => handleRevokeSession(session.id)}
                          >
                            Выйти из этой сессии
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {!sessions.length && <p className="account-empty">Сессии пока не обнаружены.</p>}
                </div>
              </div>
            </div>
          </article>
        </section>

        {isAdmin && (
          <section className="account-admin">
            <div className="account-admin-head">
              <div>
                <span className="account-kicker">Администрирование</span>
                <h2>Права пользователей</h2>
              </div>
              <input
                type="search"
                className="account-search"
                placeholder="Поиск по логину"
                value={search}
                onInput={(event) => setSearch(event.target.value)}
              />
            </div>

            {saveState && <div className="account-status">{saveState}</div>}
            {usersError && <div className="account-status error">{usersError}</div>}
            {usersLoading && <div className="account-status">Загружаем список пользователей...</div>}

            {!usersLoading && !usersError && (
              <div className="account-users">
                {visibleUsers.map((entry) => (
                  <article key={entry.login} className="account-user-card">
                    <div className="account-user-head">
                      <div>
                        <strong>@{entry.login}</strong>
                        <span>{entry.email || 'Без email'}</span>
                      </div>
                      <span>{formatDateTime(entry.createdAt)}</span>
                    </div>

                    <div className="account-user-permissions">
                      {Object.entries(PERMISSION_LABELS).map(([permission, label]) => (
                        <label key={`${entry.login}-${permission}`} className="account-toggle">
                          <input
                            type="checkbox"
                            checked={Boolean(entry.permissions?.[permission])}
                            onChange={() => handlePermissionToggle(
                              entry.login,
                              permission,
                              Boolean(entry.permissions?.[permission])
                            )}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </article>
                ))}

                {!visibleUsers.length && (
                  <p className="account-empty">По вашему запросу пользователи не найдены.</p>
                )}
              </div>
            )}
          </section>
        )}
      </section>
    </main>
  );
};

export default AccountPage;
